import type { Block, Portal, Section } from "../types";
import { getSectionImages } from "../autoImages";

function escapeHtml(input: string): string {
	return input
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;")
		.replace(/'/g, "&#39;");
}

async function imageToDataUrl(src: string): Promise<string> {
	try {
		const response = await fetch(src);
		const blob = await response.blob();
		return await new Promise<string>((resolve, reject) => {
			const reader = new FileReader();
			reader.onloadend = () => resolve(reader.result as string);
			reader.onerror = reject;
			reader.readAsDataURL(blob);
		});
	} catch {
		return src;
	}
}

async function blockToHtml(block: Block): Promise<string> {
	switch (block.type) {
		case "heading": {
			const tag = `h${block.level}`;
			return `<${tag}>${escapeHtml(block.text)}</${tag}>`;
		}
		case "paragraph":
			return `<p>${escapeHtml(block.text)}</p>`;
		case "image": {
			const dataUrl = await imageToDataUrl(block.src);
			const caption = block.caption
				? `<p style="text-align:center;font-style:italic;color:#666;font-size:12px;">${escapeHtml(
						block.caption,
					)}</p>`
				: "";
			return `<p style="text-align:center;"><img src="${dataUrl}" alt="${escapeHtml(
				block.alt,
			)}" style="max-width:600px;"/></p>${caption}`;
		}
		case "list": {
			const tag = block.ordered ? "ol" : "ul";
			const items = block.items
				.map((item) => `<li>${escapeHtml(item)}</li>`)
				.join("");
			return `<${tag}>${items}</${tag}>`;
		}
		case "callout": {
			const color =
				block.kind === "warning"
					? "#faad14"
					: block.kind === "success"
						? "#52c41a"
						: "#1677ff";
			return `<blockquote style="border-left:4px solid ${color};padding:8px 12px;margin:12px 0;background:#fafafa;">${escapeHtml(
				block.text,
			)}</blockquote>`;
		}
		case "steps": {
			const items = block.items
				.map(
					(item) =>
						`<li><strong>${escapeHtml(item.title)}</strong>${
							item.description
								? `<br/><span>${escapeHtml(item.description)}</span>`
								: ""
						}</li>`,
				)
				.join("");
			return `<ol>${items}</ol>`;
		}
		case "table": {
			const headerRow = block.headers
				.map(
					(h) =>
						`<th style="border:1px solid #d1d5db;padding:4pt 6pt;background:#f3f4f6;text-align:left;">${escapeHtml(h)}</th>`,
				)
				.join("");
			const bodyRows = block.rows
				.map((row) => {
					const cells = row
						.map(
							(c) =>
								`<td style="border:1px solid #d1d5db;padding:4pt 6pt;vertical-align:top;">${escapeHtml(c)}</td>`,
						)
						.join("");
					return `<tr>${cells}</tr>`;
				})
				.join("");
			const caption = block.caption
				? `<p style="text-align:center;font-style:italic;color:#666;font-size:12px;">${escapeHtml(block.caption)}</p>`
				: "";
			return `<table style="border-collapse:collapse;margin:0.6em 0;width:100%;"><thead><tr>${headerRow}</tr></thead><tbody>${bodyRows}</tbody></table>${caption}`;
		}
	}
}

async function blocksToHtml(blocks: Block[]): Promise<string> {
	const parts = await Promise.all(blocks.map(blockToHtml));
	return parts.join("\n");
}

async function autoImagesToHtml(
	portal: Portal,
	sectionKey: string,
): Promise<string> {
	const images = getSectionImages(portal, sectionKey);
	if (images.length === 0) return "";
	const figures = await Promise.all(
		images.map(async (img) => {
			const dataUrl = await imageToDataUrl(img.src);
			return `<p style="text-align:center;"><img src="${dataUrl}" alt="${escapeHtml(
				img.name,
			)}" style="max-width:600px;"/></p><p style="text-align:center;font-style:italic;color:#666;font-size:12px;">${escapeHtml(
				img.name,
			)}</p>`;
		}),
	);
	return `<h3>Screenshots</h3>${figures.join("\n")}`;
}

export async function sectionToHtml(
	section: Section,
	portal: Portal,
): Promise<string> {
	const body = await blocksToHtml(section.blocks);
	const screenshots = await autoImagesToHtml(portal, section.key);
	return `<h1>${escapeHtml(section.label)}</h1><p><em>${escapeHtml(
		section.intro,
	)}</em></p>${body}${screenshots}`;
}

export async function sectionsToHtml(
	sections: Section[],
	title: string,
	portal: Portal,
): Promise<string> {
	const parts: string[] = [`<h1>${escapeHtml(title)}</h1>`];
	for (let i = 0; i < sections.length; i++) {
		const section = sections[i];
		const sectionBody = await blocksToHtml(section.blocks);
		const screenshots = await autoImagesToHtml(portal, section.key);
		const pageBreak =
			i > 0 ? `<br style="page-break-before:always;clear:both;"/>` : "";
		parts.push(
			`${pageBreak}<h2>${escapeHtml(section.label)}</h2><p><em>${escapeHtml(
				section.intro,
			)}</em></p>${sectionBody}${screenshots}`,
		);
	}
	return parts.join("\n");
}
